/* ============================================================
   WriteGuard — stops a LAPSED account from writing, without hiding its data.

   `expired` is deliberately not `none` (docs/subscriptions.md §6). A subscriber
   whose plan ran out keeps READ access: their habits, tasks and history stay
   visible, and account deletion stays reachable. What they lose is the ability
   to change anything.

   **Why here and not in `api.tsx`.** Every mutation funnels through `enqueue()`,
   which looks like the natural choke point and is the wrong one: the store
   applies the optimistic local change FIRST and enqueues after, so blocking the
   enqueue would leave the edit on screen and never send it — silent divergence
   between device and server, which is worse than either allowing or refusing.
   The guard therefore wraps the public store functions, before any local state
   is touched.

   **Why a wrapper provider and not a flag inside the store.** This re-provides
   the same `StoreContext` with mutating members swapped out, so `api.tsx` never
   learns that entitlement exists and every screen keeps calling `useStore()`
   unchanged. One file owns the policy, and the list below is the whole of it.
   ============================================================ */
import React, { useContext, useMemo } from 'react';

import { useEntitlement } from '../billing/EntitlementContext';
import { StoreContext } from './context';
import type { StoreValue } from './types';

/** Every member that CHANGES something.

    ⚠️ An allowlist, not a denylist, and deliberately so: a new selector added to
    the store must not silently become blockable, and — far more importantly — a
    new mutation must not silently become unguarded. Anything absent here stays
    callable, so this list is reviewed when the store's surface changes. */
const MUTATIONS = [
  'createProject', 'updateProject', 'setProjectArchived', 'deleteProject',
  'createTag', 'updateTag', 'deleteTag',
  'completeItem', 'togglePin', 'toggleStep', 'moveLastToLater', 'replanTo',
  'rescheduleTask', 'updateTask', 'updateHabit', 'moveTask', 'moveHabit',
  'deleteHabit', 'restoreHabit', 'skipTask', 'unskipTask', 'deleteTask',
  'restoreTask', 'undoDone', 'saveCapture',
] as const satisfies readonly (keyof StoreValue)[];

/* Deliberately NOT guarded, and each for a reason:
   - `wipeLocal` — part of account deletion. A lapsed user must always be able
     to leave (Guideline 5.1.1(v)); blocking it would make the account a trap.
   - `resync` — a read. Blocking it would freeze the data they are still
     entitled to see, and would also stop the very sync that learns they have
     resubscribed.
   - every selector — reading is what `expired` still permits. */

/** What a blocked mutation returns.

    The callers expect real values (`createProject` returns an id,
    `completeItem` a `{ celebrated }`), so returning `undefined` would crash a
    screen rather than refuse a write. These stand in for "nothing happened". */
function blockedResult(name: string): unknown {
  if (name === 'completeItem') return { celebrated: false };
  if (name === 'moveLastToLater') return false;
  // `createProject` / `createTag` / `saveCapture` hand back an id; an empty
  // string is falsy, so a caller that branches on it takes the "nothing was
  // created" path instead of navigating to a row that does not exist.
  return '';
}

export function WriteGuard({ children }: { children: React.ReactNode }) {
  const store = useContext(StoreContext);
  const { canWrite, requestUpgrade } = useEntitlement();

  const guarded = useMemo<StoreValue | null>(() => {
    if (!store || canWrite) return store;
    const next = { ...store } as Record<string, unknown>;
    for (const name of MUTATIONS) {
      next[name] = (..._args: unknown[]) => {
        // Surface the paywall rather than failing silently. A tap that does
        // nothing and says nothing reads as a broken app, and the user cannot
        // act on what they are not told.
        requestUpgrade();
        return blockedResult(name);
      };
    }
    return next as unknown as StoreValue;
  }, [store, canWrite, requestUpgrade]);

  // No provider mounted above us — nothing to guard, and re-providing `null`
  // would break `useStore()`'s own error.
  if (!store) return <>{children}</>;
  return <StoreContext.Provider value={guarded}>{children}</StoreContext.Provider>;
}
